import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import { useNotification } from '../contexts/NotificationContext';
import { useAuth } from '../contexts/AuthContext';
import GitHubIntegration from './GitHubIntegration';
import EmojiIcon from './EmojiIcon';
import { iconMap } from './IconMap';
import InfoPanel from './InfoPanel';
import githubLogo from '../assets/pnggithub.png';
import './ProjectManagement.css';
import { getProjectRoleBadgeInfo, getGlobalUserRoleBadgeInfo } from '../utils/roleBadges';

// Limites de caracteres para Projetos
const PROJECT_LIMITS = {
  name: 50,
  description: 300,
  icon: 10,
};

const ProjectManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { showSuccess, showError } = useNotification();
  const { user, hasPermission } = useAuth();
  const [projects, setProjects] = useState([]);
  const [projectSearch, setProjectSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [showGitHubIntegration, setShowGitHubIntegration] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    status: 'active',
    icon: '',
    is_demo: false,
    default_role_id: ''
  });
  const [editActiveTab, setEditActiveTab] = useState('details');
  const [availableStatuses, setAvailableStatuses] = useState([
    { key: 'active', label: 'Ativo', color: '#28a745' },
    { key: 'inactive', label: 'Inativo', color: '#6c757d' },
    { key: 'completed', label: 'Concluído', color: '#007bff' }
  ]);
  // Snapshot inicial para comparar alterações (estado "dirty" em edição)
  const [initialFormData, setInitialFormData] = useState(null);
  const [roles, setRoles] = useState([]);
  const [canCreateProject, setCanCreateProject] = useState(false);
  const [showDeleteProjectModal, setShowDeleteProjectModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteProjectLoading, setDeleteProjectLoading] = useState(false);
  // Erros de validação por campo dentro do modal
  const [formErrors, setFormErrors] = useState({});
  // Acesso a projetos
  const [accessModalProjectId, setAccessModalProjectId] = useState(null);
  const [accessUsers, setAccessUsers] = useState([]);
  const [accessGroups, setAccessGroups] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [groupSearchTerm, setGroupSearchTerm] = useState('');
  const [foundUsers, setFoundUsers] = useState([]);
  const [foundGroups, setFoundGroups] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [accessActiveTab, setAccessActiveTab] = useState('users'); // 'users' | 'groups'

  // Util padronizada para badges de roles de projeto
  const getRoleBadgeInfo = (roleKey) => getProjectRoleBadgeInfo(roleKey);

  // Helper: obter company_id do projeto do modal de acesso
  const getAccessProjectCompanyId = useCallback(() => {
    const proj = projects.find(p => p.id === accessModalProjectId);
    return proj?.company_id ?? null;
  }, [accessModalProjectId, projects]);
  const [canViewProjects, setCanViewProjects] = useState(false);
  const iconChoices = Object.keys(iconMap);
  const roleUpper = String(user?.role || '').toUpperCase();
  const isIndividualAccount = roleUpper === 'TRIAL' || roleUpper === 'UNITARIO';
  const shouldHighlightGithub = new URLSearchParams(location.search).get('focus') === 'github';

  const handleGitHubIntegrationToggle = (projectId) => {
    setShowGitHubIntegration(showGitHubIntegration === projectId ? null : projectId);
    if (shouldHighlightGithub) {
      navigate('/projects', { replace: true });
    }
  };

  // Guard de acesso: somente ADM/ADMIN/GERENCIAL podem acessar a tela de Projetos
  useEffect(() => {
    const allowed = hasPermission('canManageProjects', ['ADM', 'ADMIN', 'GERENCIAL']);
    if (!allowed) {
      showError('Acesso negado: apenas perfis Gerencial ou ADM podem acessar Projetos.');
      navigate('/overview');
      setCanViewProjects(false);
      return;
    }
    setCanViewProjects(true);
  }, [hasPermission, navigate, showError]);

  useEffect(() => {
    const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await axios.get(getApiUrl('projects'));
      
      if (response.data.success) {
        setProjects(response.data.projects);
      } else {
        console.error('Erro ao buscar projetos:', response.data.error);
        if (response?.data?.error) {
          showError(response.data.error);
        }
      }
    } catch (error) {
      console.error('Erro ao buscar projetos:', error);
      const serverMessage = error?.response?.data?.error || error?.response?.data?.message;
      if (serverMessage) {
        showError(serverMessage);
      } else {
        showError('Erro ao buscar projetos. Usando dados mock temporariamente.');
      }
      // Se não conseguir conectar com a API, usar dados mock
      setProjects([
        {
          id: 1,
          name: 'Sistema de E-commerce',
          description: 'Testes para plataforma de vendas online',
          status: 'active',
          created_at: '2024-01-15T10:00:00Z'
        },
        {
          id: 2,
          name: 'App Mobile Banking',
          description: 'Testes de segurança e usabilidade',
          status: 'active',
          created_at: '2024-01-10T14:30:00Z'
        }
      ]);
    } finally {
      setLoading(false);
    }
    };
    // Não buscar projetos se usuário não tiver acesso
    if (!canViewProjects) return;
    fetchProjects();
  }, [canViewProjects, showError]);

  const fetchRoles = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl('roles'), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        const data = await response.json();
        setRoles(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Erro ao carregar roles:', error);
    }
  }, []);

  const fetchProjectStatuses = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ domain: 'project', is_active: 'true' });
      const res = await fetch(getApiUrl(`status?${params}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data.data) ? data.data : [];
        const mapped = list.map(s => ({ key: s.key, label: s.label || s.key, color: s.color || '#6c757d' }));
        if (mapped.length > 0) setAvailableStatuses(mapped);
      }
    } catch (e) {
    }
  }, []);

  const fetchProjectStatusOptions = useCallback(async (projectId) => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ domain: 'project', hidden: 'false' });
      const res = await fetch(getApiUrl(`status-overrides/${projectId}?${params}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          const list = Array.isArray(data.data) ? data.data : [];
          if (list.length > 0) {
            const mapped = list.map(s => ({ key: s.key, label: s.label || s.key, color: s.color || '#6c757d' }));
            setAvailableStatuses(mapped);
            // garantir seleção válida
            const keys = mapped.map(m => m.key);
            if (!keys.includes(formData.status)) {
              setFormData(prev => ({ ...prev, status: mapped[0].key }));
            }
            return;
          }
        }
      }
      // fallback para listar status de domínio projeto
      await fetchProjectStatuses();
    } catch (e) {
      await fetchProjectStatuses();
    }
  }, [fetchProjectStatuses, formData.status]);


  useEffect(() => {
    // Carregar roles ao abrir o modal de criação/edição
    if (showCreateForm || accessModalProjectId) {
      fetchRoles();
    }
  }, [showCreateForm, accessModalProjectId, fetchRoles]);

  useEffect(() => {
    if (showCreateForm) {
      fetchProjectStatuses();
    }
  }, [showCreateForm, fetchProjectStatuses]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search || '');
    const githubAppStatus = params.get('githubApp');
    const installationId = params.get('installationId');
    const projectId = params.get('projectId');

    if (githubAppStatus === 'installed' && installationId && projectId) {
      setShowGitHubIntegration(projectId);
    }
  }, []);


  useEffect(() => {
    // Permissão baseada na chave do backend; fallback para ADM/ADMIN
    const canCreate = hasPermission('canManageProjects', ['ADM', 'ADMIN']);
    setCanCreateProject(!!canCreate);
  }, [hasPermission]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Validações client-side conforme card v0.01
      if (!canCreateProject) {
        showError('Você não tem permissão para criar projetos.');
        return;
      }
      const trimmedName = (formData.name || '').trim();
      const trimmedDescription = (formData.description || '').trim();
      // Coletar erros por campo para exibir inline
      const newErrors = {};
      if (!trimmedName) {
        newErrors.name = 'O Nome do projeto é obrigatório.';
      }
      if (!trimmedDescription) {
        newErrors.description = 'O Resumo do projeto é obrigatório.';
      }
      if ((formData.description || '').length > 300) {
        newErrors.description = 'O Resumo não deve exceder 300 caracteres.';
      }
      if ((formData.icon || '').length > PROJECT_LIMITS.icon) {
        newErrors.icon = `O Ícone não deve exceder ${PROJECT_LIMITS.icon} caracteres.`;
      }
      if (Object.keys(newErrors).length > 0) {
        setFormErrors(newErrors);
        return;
      }
      if (editingProject) {
        // Atualizar projeto existente
        const payload = {
          name: trimmedName,
          description: trimmedDescription,
          status: formData.status,
          icon: formData.icon || '',
          is_demo: !!formData.is_demo,
          default_role_id: formData.default_role_id || null
        };
        const response = await axios.put(getApiUrl(`projects/${editingProject.id}`), payload);
        
        if (response.data.success) {
          setProjects(projects.map(p => 
            p.id === editingProject.id ? response.data.project : p
          ));
          setEditingProject(null);
          setInitialFormData(null);
          // Fechar o modal de criação/edição após atualizar
          setShowCreateForm(false);
          showSuccess('Projeto atualizado com sucesso!');
        } else {
          showError(response.data.error || 'Erro ao atualizar projeto');
        }
      } else {
        // Criar novo projeto
        const payload = {
          name: trimmedName,
          description: trimmedDescription,
          status: formData.status,
          icon: formData.icon || '',
          is_demo: !!formData.is_demo
        };
        const response = await axios.post(getApiUrl('projects'), payload);
        
        if (response.data.success) {
          setProjects([response.data.project, ...projects]);
          setShowCreateForm(false);
          showSuccess('Projeto criado com sucesso!');
        } else {
          showError(response.data.error || 'Erro ao criar projeto');
        }
      }
      
      // Limpar formulário
      setFormData({ name: '', description: '', status: 'active', icon: '', is_demo: false, default_role_id: '' });
      setFormErrors({});
      setInitialFormData(null);
      
  } catch (error) {
    console.error('Erro ao salvar projeto:', error);
    const status = error?.response?.status;
    const serverMessage = error?.response?.data?.error || error?.response?.data?.message;
    // Mapear conflito de nome para erro inline no campo Nome
    if (status === 409 && serverMessage && /projeto com este nome/i.test(serverMessage)) {
      setFormErrors((prev) => ({ ...prev, name: serverMessage }));
    } else {
      showError(serverMessage || 'Erro ao salvar projeto. Verifique sua conexão.');
    }
  }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      description: project.description,
      status: project.status,
      icon: project.icon || '',
      is_demo: !!project.is_demo,
      default_role_id: project.default_role_id || ''
    });
    setEditActiveTab('details');
    // Snapshot inicial para detectar alterações
    setInitialFormData({
      name: project.name || '',
      description: project.description || '',
      status: project.status || 'active',
      icon: project.icon || '',
      is_demo: !!project.is_demo,
      default_role_id: project.default_role_id || ''
    });
    setShowCreateForm(true);
    fetchProjectStatusOptions(project.id);
  };

  const openDeleteProjectModal = (project) => {
    setProjectToDelete(project);
    setShowDeleteProjectModal(true);
  };

  const closeDeleteProjectModal = () => {
    if (deleteProjectLoading) return;
    setShowDeleteProjectModal(false);
    setProjectToDelete(null);
  };

  const confirmDeleteProject = async () => {
    const projectId = projectToDelete?.id;
    if (!projectId) {
      closeDeleteProjectModal();
      return;
    }

    try {
      setDeleteProjectLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.delete(getApiUrl(`projects/${projectId}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setProjects(prev => (Array.isArray(prev) ? prev : []).filter(p => p.id !== projectId));
        showSuccess('Projeto deletado com sucesso!');
        closeDeleteProjectModal();
      } else {
        showError(response.data.error || 'Erro ao deletar projeto');
      }
    } catch (error) {
      console.error('Erro ao deletar projeto:', error);
      const serverMessage = error?.response?.data?.error || error?.response?.data?.message;
      if (serverMessage) {
        showError(serverMessage);
      } else if (error?.response?.status === 403) {
        showError('Acesso negado: você não possui permissão para deletar projetos.');
      } else if (error?.response?.status === 404) {
        showError('Projeto não encontrado ou você não tem acesso a ele.');
      } else {
        showError('Erro ao deletar projeto. Verifique sua conexão.');
      }
    } finally {
      setDeleteProjectLoading(false);
    }
  };

  // --------- Acesso ao Projeto ---------
  const openAccessModal = async (projectId) => {
    setAccessModalProjectId(projectId);
    setFoundUsers([]);
    setFoundGroups([]);
    setUserSearchTerm('');
    setGroupSearchTerm('');
    setSelectedRoleId('');
    await fetchProjectAccess(projectId);
  };

  const closeAccessModal = () => {
    setAccessModalProjectId(null);
    setAccessUsers([]);
    setAccessGroups([]);
    setFoundUsers([]);
    setFoundGroups([]);
    setSelectedRoleId('');
  };

  const fetchProjectAccess = useCallback(async (projectId) => {
    try {
      setAccessLoading(true);
      const token = localStorage.getItem('token');

      const [usersRes, groupsRes] = await Promise.all([
        fetch(getApiUrl(`projects/${projectId}/access/users`), {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(getApiUrl(`projects/${projectId}/access/groups`), {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const usersData = await usersRes.json();
      const groupsData = await groupsRes.json();

      setAccessUsers(usersData.success ? usersData.data : []);
      setAccessGroups(groupsData.success ? groupsData.data : []);
    } catch (error) {
      console.error('Erro ao carregar acesso do projeto:', error);
      showError('Erro ao carregar acesso do projeto.');
    } finally {
      setAccessLoading(false);
    }
  }, [showError]);

  const mapRoleNameToLegacy = (name) => {
    const n = String(name || '').toLowerCase();
    if (n === 'admin' || n === 'adm') return 'ADM';
    if (n === 'manager' || n === 'gerencial' || n === 'leader') return 'GERENCIAL';
    if (n === 'tester' || n === 'qa') return 'QA';
    if (n === 'developer' || n === 'dev' || n === 'viewer' || n === 'consulta' || n === 'programador') return 'DEV';
    return String(name || '').toUpperCase();
  };

  const searchUsers = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ page: 1, limit: 100 });
      if (userSearchTerm) params.append('search', userSearchTerm);
      const companyId = getAccessProjectCompanyId();
      if (companyId) params.append('company_id', companyId);
      const selectedRoleObj = roles.find(r => r.id === selectedRoleId);
      if (selectedRoleObj && selectedRoleObj.name) {
        params.append('role', mapRoleNameToLegacy(selectedRoleObj.name));
      }
      const res = await fetch(getApiUrl(`users?${params}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data.data) ? data.data : [];
        const filteredByCompany = companyId ? list.filter(u => u.company_id === companyId) : list;
        const legacyRole = selectedRoleObj && selectedRoleObj.name ? mapRoleNameToLegacy(selectedRoleObj.name) : '';
        const filteredByRole = legacyRole
          ? filteredByCompany.filter(u => mapRoleNameToLegacy(u.role) === legacyRole)
          : filteredByCompany;
        setFoundUsers(filteredByRole);
      } else {
        let msg = 'Erro ao buscar usuários.';
        try {
          const data = await res.json();
          msg = data?.message || data?.error || msg;
        } catch {}
        showError(msg);
      }
    } catch (error) {
      console.error('Erro na busca de usuários:', error);
      showError('Erro na busca de usuários.');
    }
  }, [userSearchTerm, getAccessProjectCompanyId, selectedRoleId, roles, showError]);

  const searchGroups = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (groupSearchTerm) params.append('search', groupSearchTerm);
      const res = await fetch(getApiUrl(`groups?${params}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data.data)
            ? data.data
            : Array.isArray(data.results)
              ? data.results
              : [];
        setFoundGroups(list);
      }
    } catch (error) {
      console.error('Erro na busca de grupos:', error);
    }
  }, [groupSearchTerm]);

  const selectedRoleObjForAccess = roles.find(r => r.id === selectedRoleId);
  const legacyRoleForAccess = selectedRoleObjForAccess && selectedRoleObjForAccess.name
    ? mapRoleNameToLegacy(selectedRoleObjForAccess.name)
    : '';
  const foundUsersByRole = legacyRoleForAccess
    ? foundUsers.filter(u => mapRoleNameToLegacy(u.role) === legacyRoleForAccess)
    : foundUsers;

  const addUserAccess = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`projects/${accessModalProjectId}/access/users`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: userId, role_id: selectedRoleId || null })
      });
      const data = await res.json();
      if (data.success) {
        showSuccess('Acesso de usuário atualizado.');
        await fetchProjectAccess(accessModalProjectId);
      } else {
        showError(data.error || 'Erro ao conceder acesso ao usuário');
      }
    } catch (error) {
      console.error('Erro ao conceder acesso ao usuário:', error);
      showError('Erro ao conceder acesso ao usuário.');
    }
  };

  const removeUserAccess = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`projects/${accessModalProjectId}/access/users/${userId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        showSuccess('Acesso de usuário removido.');
        await fetchProjectAccess(accessModalProjectId);
      } else {
        showError(data.error || 'Erro ao remover acesso do usuário');
      }
    } catch (error) {
      console.error('Erro ao remover acesso do usuário:', error);
      showError('Erro ao remover acesso do usuário.');
    }
  };

  const addGroupAccess = async (groupId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`projects/${accessModalProjectId}/access/groups`), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ group_id: groupId, role_id: selectedRoleId || null })
      });
      const data = await res.json();
      if (data.success) {
        showSuccess('Acesso de grupo atualizado.');
        await fetchProjectAccess(accessModalProjectId);
      } else {
        showError(data.error || 'Erro ao conceder acesso ao grupo');
      }
    } catch (error) {
      console.error('Erro ao conceder acesso ao grupo:', error);
      showError('Erro ao conceder acesso ao grupo.');
    }
  };

  const removeGroupAccess = async (groupId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(getApiUrl(`projects/${accessModalProjectId}/access/groups/${groupId}`), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        showSuccess('Acesso de grupo removido.');
        await fetchProjectAccess(accessModalProjectId);
      } else {
        showError(data.error || 'Erro ao remover acesso do grupo');
      }
    } catch (error) {
      console.error('Erro ao remover acesso do grupo:', error);
      showError('Erro ao remover acesso do grupo.');
    }
  };

  const cancelEdit = () => {
    setEditingProject(null);
    setShowCreateForm(false);
    setFormData({ name: '', description: '', status: 'active', icon: '', is_demo: false, default_role_id: '' });
    setFormErrors({});
    setInitialFormData(null);
  };

  // Abrir modal de criação com snapshot inicial vazio
  const openCreateModal = () => {
    const defaults = { name: '', description: '', status: 'active', icon: '', is_demo: false, default_role_id: '' };
    setEditingProject(null);
    setFormData(defaults);
    setFormErrors({});
    setInitialFormData(defaults);
    setShowCreateForm(true);
    setEditActiveTab('details');
  };

  // Comparação de alterações para desabilitar botão "Atualizar" quando não houver mudanças
  const isEditDirty = (() => {
    if (!editingProject) return true; // em criação, não aplicamos trava por "dirty"
    const trim = (s) => (s || '').trim();
    const cur = {
      name: trim(formData.name),
      description: trim(formData.description),
      status: formData.status,
      icon: formData.icon || '',
      is_demo: !!formData.is_demo,
      default_role_id: formData.default_role_id || ''
    };
    const init = initialFormData ? {
      name: trim(initialFormData.name),
      description: trim(initialFormData.description),
      status: initialFormData.status,
      icon: initialFormData.icon || '',
      is_demo: !!initialFormData.is_demo,
      default_role_id: initialFormData.default_role_id || ''
    } : null;
    if (!init) return true;
    return (
      cur.name !== init.name ||
      cur.description !== init.description ||
      cur.status !== init.status ||
      cur.icon !== init.icon ||
      cur.is_demo !== init.is_demo ||
      cur.default_role_id !== init.default_role_id
    );
  })();

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'completed': return '#007bff';
      case 'inactive': return '#6c757d';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Ativo';
      case 'completed': return 'Concluído';
      case 'inactive': return 'Inativo';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="project-management">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Carregando projetos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="project-management">
      {!canViewProjects && (
        <div className="unauthorized">
          <h1>🔒 Acesso negado</h1>
          <p>Apenas perfis Gerencial ou ADM podem acessar Projetos.</p>
          <button className="btn-primary" onClick={() => navigate('/overview')}>Ir para Overview</button>
        </div>
      )}
      <div style={{ display: canViewProjects ? 'block' : 'none' }}>
        <div className="project-header">
          <div>
            <h1>📋 Gestão de Projetos</h1>
            <p>Organize os projetos, acessos e integrações da sua operação de QA.</p>
          </div>
          {canCreateProject && (
            <div className="header-actions">
              <button 
                className="btn-primary"
                onClick={openCreateModal}
              >
                ➕ Novo Projeto
              </button>
            </div>
          )}
        </div>

      {showCreateForm && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingProject ? '✏️ Editar Projeto' : '➕ Criar Novo Projeto'}</h2>
              <button className="btn-close" onClick={cancelEdit}>✕</button>
            </div>
            
            <form onSubmit={handleSubmit} className="project-form">
              <div className="edit-tabs">
                <button
                  type="button"
                  className={editActiveTab === 'details' ? 'tab active' : 'tab'}
                  onClick={() => setEditActiveTab('details')}
                >Dados</button>
                <button
                  type="button"
                  className={editActiveTab === 'status' ? 'tab active' : 'tab'}
                  onClick={() => setEditActiveTab('status')}
                >Status</button>
              </div>

              {editActiveTab === 'details' && (
              <div className="form-section">
              <div className="form-group">
                <label htmlFor="name">Nome do Projeto <span className="required">*</span> (máx. {PROJECT_LIMITS.name} caracteres):</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, PROJECT_LIMITS.name);
                    setFormData({ ...formData, name: value });
                    if (formErrors.name && value.trim()) {
                      setFormErrors((prev) => ({ ...prev, name: undefined }));
                    }
                  }}
                  placeholder="Digite o nome do projeto"
                  aria-invalid={!!formErrors.name}
                  maxLength={PROJECT_LIMITS.name}
                />
                <div
                  className={(formData?.name?.length ?? 0) >= PROJECT_LIMITS.name ? 'char-counter limit' : 'char-counter'}
                  aria-live="polite"
                >
                  {(formData?.name?.length ?? 0)}/{PROJECT_LIMITS.name}
                </div>
                {formErrors.name && (
                  <div className="field-error">{formErrors.name}</div>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="description">Resumo do projeto <span className="required">*</span> (máx. {PROJECT_LIMITS.description} caracteres):</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => {
                    const val = e.target.value.slice(0, PROJECT_LIMITS.description);
                    setFormData({ ...formData, description: val });
                    if (formErrors.description && val.trim()) {
                      setFormErrors((prev) => ({ ...prev, description: undefined }));
                    }
                  }}
                  placeholder="Adicione um resumo do projeto"
                  rows="4"
                  maxLength={PROJECT_LIMITS.description}
                  aria-invalid={!!formErrors.description}
                />
                <div className={`char-counter ${formData.description.length >= PROJECT_LIMITS.description ? 'limit' : ''}`}>
                  {formData.description.length}/{PROJECT_LIMITS.description}
                </div>
                {formErrors.description && (
                  <div className="field-error">{formErrors.description}</div>
                )}
              </div>

              </div>
              )}

              {editActiveTab === 'status' && (
              <div className="form-group">
                <label htmlFor="status">Status do projeto:</label>
                <select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  {availableStatuses.map(s => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
                <div className="status-chips">
                  {availableStatuses.map(s => (
                    <span key={`chip-${s.key}`} className="status-chip" style={{ backgroundColor: s.color || getStatusColor(s.key) }}>
                      {s.label}
                    </span>
                  ))}
                </div>
              </div>
              )}

              {editActiveTab === 'details' && (
              <div className="form-group">
                <label htmlFor="icon">Ícone (emoji ou texto) (máx. {PROJECT_LIMITS.icon} caracteres):</label>
                <input
                  type="text"
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => {
                    const value = e.target.value.slice(0, PROJECT_LIMITS.icon);
                    setFormData({ ...formData, icon: value });
                    if (formErrors.icon && value.length <= PROJECT_LIMITS.icon) {
                      setFormErrors((prev) => ({ ...prev, icon: undefined }));
                    }
                  }}
                  placeholder="Ex.: 🚀"
                  maxLength={PROJECT_LIMITS.icon}
                  aria-invalid={!!formErrors.icon}
                />
                <div
                  className={(formData?.icon?.length ?? 0) >= PROJECT_LIMITS.icon ? 'char-counter limit' : 'char-counter'}
                  aria-live="polite"
                >
                  {(formData?.icon?.length ?? 0)}/{PROJECT_LIMITS.icon}
                </div>
                {formErrors.icon && (
                  <div className="field-error">{formErrors.icon}</div>
                )}
                <div className="emoji-choices">
                  {iconChoices.map((emoji) => (
                    <button
                      type="button"
                      key={emoji}
                      className={`emoji-option ${formData.icon === emoji ? 'selected' : ''}`}
                      onClick={() => setFormData({ ...formData, icon: emoji })}
                      title={emoji}
                    >
                      <EmojiIcon emoji={emoji} />
                    </button>
                  ))}
                  <button
                    type="button"
                    className="emoji-option clear"
                    onClick={() => setFormData({ ...formData, icon: '' })}
                  >
                    Limpar
                  </button>
              </div>
              </div>

              )}

              {editActiveTab === 'details' && (
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.is_demo}
                    onChange={(e) => setFormData({ ...formData, is_demo: e.target.checked })}
                  />
                  <span>Projeto de demonstração</span>
                </label>
              </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn-danger" onClick={cancelEdit}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={editingProject && !isEditDirty}>
                  {editingProject ? 'Atualizar' : 'Adicionar'} projeto
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="projects-layout">
        <div className="projects-main">
          {/* Barra de ferramentas de Projetos: busca client-side */}
          <div className="projects-toolbar">
            <div className="search-input">
              <span className="icon">🔍</span>
              <input
                type="text"
                className="project-search-input"
                placeholder="Buscar projetos..."
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
              />
            </div>
          </div>

          <div className="projects-grid">
          {(() => {
          const term = projectSearch.trim().toLowerCase();
          const visibleProjects = term
            ? projects.filter(p => (p.name || '').toLowerCase().includes(term))
            : projects;
          if (visibleProjects.length === 0) {
            return (
              <div className="empty-state">
                <h3>📋 Nenhum projeto encontrado</h3>
                <p>Ajuste sua busca ou crie um novo projeto.</p>
              </div>
            );
          }
            return visibleProjects.map(project => (
              <div key={project.id} className="project-card">
                <div className="project-header-card">
                  <h3>{project.name}</h3>
                  <div className="project-actions">
                    {!isIndividualAccount && (
                      <button 
                        className="btn-secondary"
                        onClick={() => openAccessModal(project.id)}
                        title="Gerenciar acesso"
                      >
                        🔒
                      </button>
                    )}
                    <button 
                      className={`btn-github${shouldHighlightGithub ? ' btn-github-highlight' : ''}`}
                      onClick={() => handleGitHubIntegrationToggle(project.id)}
                      title="Integração GitHub"
                    >
                      <img src={githubLogo} alt="GitHub" />
                    </button>
                    <button 
                      className="btn-edit"
                      onClick={() => handleEdit(project)}
                      title="Editar projeto"
                      disabled={project.status === 'completed'}
                    >
                      ✏️
                    </button>
                    <button 
                      className="btn-delete"
                      type="button"
                      onClick={() => openDeleteProjectModal(project)}
                      title="Deletar projeto"
                    >
                      🗑️
                    </button>
                  </div>
                </div>
                
                <p className="project-description">{project.description}</p>
                
                <div className="project-footer">
                  <span 
                    className="project-status"
                    style={{ backgroundColor: getStatusColor(project.status) }}
                  >
                    {getStatusText(project.status)}
                  </span>
                  <span className="project-date">
                    {new Date(project.created_at).toLocaleDateString('pt-BR')}
                  </span>
                </div>
                
                {showGitHubIntegration === project.id && (
                  <div className="github-integration-section">
                    <h4>🔗 Integração GitHub</h4>
                    <GitHubIntegration projectId={project.id} />
                  </div>
                )}
              </div>
            ));
          })()}
          </div>
        </div>
        <InfoPanel />
      </div>

      {accessModalProjectId && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>🔒 Acesso ao Projeto</h2>
              <button className="btn-close" onClick={closeAccessModal}>✕</button>
            </div>
            <div className="modal-body">
            <div className="access-tabs">
              <button 
                className={accessActiveTab === 'users' ? 'tab active' : 'tab'}
                onClick={() => setAccessActiveTab('users')}
              >Usuários</button>
              <button 
                className={accessActiveTab === 'groups' ? 'tab active' : 'tab'}
                onClick={() => setAccessActiveTab('groups')}
              >Grupos</button>
            </div>
            <div className="form-group">
              <label>Papel para novos acessos:</label>
              <select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
              >
                <option value="">Todos</option>
                {roles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.display_name || role.name}
                  </option>
                ))}
              </select>
            </div>

            {accessActiveTab === 'users' ? (
              <div className="access-section">
                <div className="form-group">
                  <label>Buscar usuários:</label>
                  <input
                    type="text"
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    placeholder="Digite nome ou email"
                  />
                  <button className="btn-secondary" onClick={searchUsers}>Buscar</button>
                </div>
                <div className="access-lists">
                  <div className="access-list">
                    <h3>Acesso atual</h3>
                    {accessLoading ? (
                      <p>Carregando...</p>
                    ) : accessUsers.length === 0 ? (
                      <p>Nenhum usuário com acesso.</p>
                    ) : (
                      accessUsers.map(u => {
                        const badgeProject = getRoleBadgeInfo(u.role_key || u.role_name);
                        const badgeGlobal = getGlobalUserRoleBadgeInfo(u.global_role);
                        const showGlobal = !!badgeGlobal && (!badgeProject || badgeProject.text !== badgeGlobal.text);
                        const showProject = !!badgeProject;
                        return (
                          <div key={u.user_id} className="access-item">
                            <div className="access-info">
                              <strong>{u.name}</strong> <span className="email">({u.email})</span>
                              {showGlobal && <span className={`role-badge ${badgeGlobal.class}`}> {badgeGlobal.text}</span>}
                              {showProject && <span className={`role-badge ${badgeProject.class}`}> {badgeProject.text}</span>}
                            </div>
                            <button className="btn-delete" onClick={() => removeUserAccess(u.user_id)}>Remover</button>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="access-list">
                    <h3>Adicionar usuários</h3>
                    {foundUsersByRole.length === 0 ? (
                      <p>Nenhum resultado.</p>
                    ) : (
                      foundUsersByRole.map(u => (
                        <div key={u.id} className="access-item">
                          <div className="access-info">
                            <strong>{u.first_name} {u.last_name}</strong> <span className="email">({u.email})</span>
                            {(() => {
                              const badgeGlobal = getGlobalUserRoleBadgeInfo(u.role);
                              return badgeGlobal ? <span className={`role-badge ${badgeGlobal.class}`}> {badgeGlobal.text}</span> : null;
                            })()}
                          </div>
                          <button className="btn-primary" onClick={() => addUserAccess(u.id)}>Adicionar</button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="access-section">
                <div className="form-group">
                  <label>Buscar grupos:</label>
                  <input
                    type="text"
                    value={groupSearchTerm}
                    onChange={(e) => setGroupSearchTerm(e.target.value)}
                    placeholder="Digite o nome do grupo"
                  />
                  <button className="btn-secondary" onClick={searchGroups}>Buscar</button>
                </div>
                <div className="access-lists">
                  <div className="access-list">
                    <h3>Acesso atual</h3>
                    {accessLoading ? (
                      <p>Carregando...</p>
                    ) : accessGroups.length === 0 ? (
                      <p>Nenhum grupo com acesso.</p>
                    ) : (
                      accessGroups.map(g => {
                        const badge = getRoleBadgeInfo(g.role_key || g.role_name);
                        return (
                          <div key={g.group_id} className="access-item">
                            <div className="access-info">
                              <strong>{g.name}</strong>
                              {badge && <span className={`role-badge ${badge.class}`}> {badge.text}</span>}
                            </div>
                            <button className="btn-delete" onClick={() => removeGroupAccess(g.group_id)}>Remover</button>
                          </div>
                        );
                      })
                    )}
                  </div>
                  <div className="access-list">
                    <h3>Adicionar grupos</h3>
                    {foundGroups.length === 0 ? (
                      <p>Nenhum resultado.</p>
                    ) : (
                      foundGroups.map(g => (
                        <div key={g.id} className="access-item">
                          <div className="access-info">
                            <strong>{g.name}</strong>
                          </div>
                          <button className="btn-primary" onClick={() => addGroupAccess(g.id)}>Adicionar</button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}
            </div>
          </div>
        </div>
      )}

      {showDeleteProjectModal && projectToDelete && (
        <div className="modal-overlay" onClick={closeDeleteProjectModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Excluir projeto</h2>
              <button className="btn-close" onClick={closeDeleteProjectModal} disabled={deleteProjectLoading}>✕</button>
            </div>
            <div className="modal-body">
              <p>
                Tem certeza que deseja excluir o projeto <strong>{projectToDelete.name}</strong>
                {projectToDelete.id ? ` (ID: ${projectToDelete.id})` : ''}?
              </p>
              <div style={{
                marginTop: 12,
                padding: '12px 14px',
                border: '1px solid #fca5a5',
                background: '#fef2f2',
                color: '#7f1d1d',
                borderRadius: 6
              }}>
                <strong>Atenção:</strong> Esta ação é permanente e <strong>não pode ser desfeita</strong>.
                Ao excluir o projeto, você perderá <strong>todos os dados vinculados</strong> a ele.
              </div>
            </div>
            <div className="modal-actions" style={{ padding: 20 }}>
              <button type="button" className="btn btn-secondary" onClick={closeDeleteProjectModal} disabled={deleteProjectLoading}>Cancelar</button>
              <button type="button" className="btn btn-danger" onClick={confirmDeleteProject} disabled={deleteProjectLoading}>
                {deleteProjectLoading ? 'Excluindo...' : 'Excluir definitivamente'}
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default ProjectManagement;
